import {
  addDays,
  computeLunchPlacement,
  isWeekend,
  minutesToTime,
  parseTimeToMinutes,
} from 'src/components/Scheduling/Timeline/timeUtils';
import { utcToZoned } from 'src/helper/date';
import type {
  AvailabilityWindow,
  BlockoutBlock,
  EmergencyRescheduleAffectedWO,
  EmergencyRescheduleProposal,
  LunchWindow,
  ScheduleBlock,
  SchedulingLocation,
  SchedulingState,
  SchedulingTechnician,
  SchedulingWorkOrder,
  TechnicianDailyStats,
  WorkOrderPriority,
  WorkOrderStatus,
} from './types';
import { schedulingInitialState } from './types';

/**
 * moment.utc(x).tz(tz).format(fmt) → the adapter's UTC→zone formatter. Bit-identical to
 * the old chain for every conforming backend UTC instant (Z / +00:00 / microseconds /
 * offset-less), across DST transitions and day boundaries. The adapter's ISO parse is
 * strict (S-05 — kills dayjs's silent out-of-range rollover), so two NON-conforming
 * shapes moment happened to accept — an hour-24 instant (`…T24:00:00Z`) and a 2-digit
 * offset (`…+00`) — resolve to '' here instead; the optimizer/API never emits either,
 * and a lenient fallback would reintroduce the exact rollover S-05 removes. '' is
 * therefore only ever returned for genuinely empty/invalid input.
 */
const fmtInZone = (utcDateTime: string, timezone: string, format: string): string => {
  const out = utcToZoned<string>(utcDateTime, timezone, format);
  return typeof out === 'string' ? out : '';
};

// ===============================
// Utilization helpers (exported for use in reducers)
// ===============================

/** Merges overlapping time intervals and returns total covered minutes. */
export function mergeIntervalsMinutes(intervals: [number, number][]): number {
  if (intervals.length === 0) return 0;
  const sorted = intervals.slice().sort((a, b) => a[0] - b[0]);
  let total = 0;
  let [curStart, curEnd] = sorted[0];
  for (let i = 1; i < sorted.length; i++) {
    const [start, end] = sorted[i];
    if (start < curEnd) {
      curEnd = Math.max(curEnd, end);
    } else {
      total += curEnd - curStart;
      [curStart, curEnd] = [start, end];
    }
  }
  total += curEnd - curStart;
  return total;
}

/**
 * Day-view utilization formula (1 decimal place):
 *   scheduledMinutes / (maxWorkHoursPerDay * 60) * 100
 * PTO is intentionally excluded from the denominator.
 */
export function calcDayUtilization(
  scheduledMinutes: number,
  _ptoMinutes: number,
  maxWorkHoursPerDay: number,
): number {
  const availableMinutes = maxWorkHoursPerDay * 60;
  if (availableMinutes <= 0) return 0;
  return Math.round((scheduledMinutes / availableMinutes) * 1000) / 10;
}

/** Returns merged PTO minutes for a given date from an array of normalized blocks. */
function blockMinutesForDate(
  blocks: Array<{ date: string; startTime: string; endTime: string }>,
  date: string,
): number {
  const intervals: [number, number][] = [];
  for (const block of blocks) {
    if (block.date !== date) continue;
    const start = parseTimeToMinutes(block.startTime);
    const end = parseTimeToMinutes(block.endTime);
    if (Number.isFinite(start) && Number.isFinite(end) && end > start) intervals.push([start, end]);
  }
  return mergeIntervalsMinutes(intervals);
}

/** Returns merged PTO minutes for a technician/date from the blockoutBlocks state map. */
function ptoMinutesFromState(
  blockoutBlocksById: Record<string, BlockoutBlock>,
  technicianId: string,
  date: string,
): number {
  const intervals: [number, number][] = [];
  for (const block of Object.values(blockoutBlocksById)) {
    if (block.technicianId !== technicianId || block.date !== date) continue;
    const start = parseTimeToMinutes(block.startTime);
    const end = parseTimeToMinutes(block.endTime);
    if (Number.isFinite(start) && Number.isFinite(end) && end > start) intervals.push([start, end]);
  }
  return mergeIntervalsMinutes(intervals);
}

// ===============================
// Raw API shapes
// ===============================

export interface RawWindow {
  start: string;
  end: string;
  technician_appointment_id?: string;
  title?: string;
  description?: string | null;
  is_block_out_all_day?: boolean;
  work_order?: { work_order_id: string; work_order_number: string } | null;
  block_out_group_uuid?: string | null;
  group_start_date?: string | null;
  group_end_date?: string | null;
}

interface RawLunchTime extends RawWindow {
  lunch_duration_minutes: number;
}

interface RawLocation {
  latitude: number | null;
  longitude: number | null;
  street_address: string | null;
  unit_number: string | null;
  city: string | null;
  state_code: string | null;
  postal_zip_code: string | null;
}

interface RawSkill {
  diagnosis_uuid: string;
  diagnosis_slug: string;
  category: string;
  sub_category: string;
}

interface RawMovedByLandon {
  reason?: string;
  from_technician: { technician_uuid: string; name: string | null };
  to_technician: { technician_uuid: string; name: string | null };
  from_start_time: string;
  from_end_time: string;
  resident_window_changed?: boolean;
  moved_at?: string;
}

interface RawWorkOrder {
  work_order_uuid: string;
  work_order_number: string;
  location: RawLocation;
  priority: string;
  job_type: string | null;
  job_classification: string | null;
  estimated_duration_minutes: number;
  locked: boolean;
  // Deliberately NOT added to RawJob below: the job entry's `locked` comes from the
  // optimizer's fixed-work-order partition, not from a WorkOrderLock row (ORI-569).
  lock_type?: string | null;
  required_skills: RawSkill[];
  resident_availability_windows: RawWindow[];
  created_at: string | null;
  resident_availability_request_pending?: boolean;
  service_request_uuid?: string;
  moved_by_landon?: RawMovedByLandon | null;
}

type RawScheduledWorkOrder = RawWorkOrder;
interface RawAwaitingAvailabilityWorkOrder extends RawWorkOrder {
  current_appointment: string | null;
}

interface RawJob {
  work_order_uuid: string;
  scheduled_date: string;
  scheduled_start: string;
  scheduled_end: string;
  travel_time_minutes: number | null;
  estimated_duration_minutes: number;
  location: RawLocation;
  priority: string;
  locked: boolean;
}

interface RawDailySchedule {
  technician_uuid: string;
  technician_name: string;
  date: string;
  jobs: RawJob[];
  total_work_hours: number;
  total_travel_hours: number;
  travel_time_to_base_minutes: number;
  utilization_percentage: number;
  recommended_lunch_start_time?: string;
  recommended_lunch_end_time?: string;
}

interface RawTechnician {
  technician_uuid: string;
  user_uuid?: string | null;
  name: string | null;
  home_location: RawLocation;
  hq_location: RawLocation;
  skills: RawSkill[];
  availability_windows: RawWindow[];
  lunch_time: RawLunchTime[];
  block_out_windows?: RawWindow[];
  lunch_duration_minutes: number;
  max_work_hours_per_day: number;
  status: string;
  display_color: string | null;
}

export interface RawSchedulingResponse {
  availability_request_pending_service_request_uuids?: string[];
  horizon: { start_date: string; days: number };
  /**
   * The MARKET's zone (not the org's, not the property's) — always present.
   * `InitialSchedulingDataPresenter.php:72` emits it from a horizon typed
   * `array{start: CarbonInterface, days: int, timezone: string}` at `:28`, so the
   * producer cannot omit it. Required here so no caller can silently substitute a
   * default for it.
   */
  timezone: string;
  unscheduled_work_orders: RawWorkOrder[];
  scheduled_work_orders: RawScheduledWorkOrder[];
  technicians: RawTechnician[];
  existing_daily_schedules: RawDailySchedule[];
  awaiting_availability_work_orders: RawAwaitingAvailabilityWorkOrder[];
}

type DaySchedule = {
  technician_uuid: string;
  date: string;
  jobs: { work_order_uuid: string }[];
  total_work_hours: number;
  total_travel_hours: number;
  travel_time_to_base_minutes: number;
  utilization_percentage: number;
  recommended_lunch_start_time?: string; // ISO UTC datetime — present after optimization
  recommended_lunch_end_time?: string; // ISO UTC datetime — present after optimization
};
type RawAssignment = {
  work_order_uuid: string;
  technician_uuid: string;
  scheduled_start_utc: string;
  scheduled_end_utc: string;
  date: string;
  travel_time_minutes: number;
  estimated_duration_minutes: number;
  location: RawLocation;
  priority: string;
  technician_name: string;
  work_order_number: string;
  work_order_address: string;
  is_reschedule?: boolean;
  current_appointment?: { technician_uuid: string | null } | null;
};

// ===============================
// Field mappers (snake_case → camelCase)
// ===============================

const toIds = (record: Record<string, unknown>): string[] => Object.keys(record);

/**
 * Calendar day a UTC instant falls on in the schedule's timezone. The wire `date`
 * fields are not local calendar days, so every day that is displayed or used as a
 * join key is derived from the instant instead.
 */
const localDay = (utcDateTime: string, timezone: string): string =>
  fmtInZone(utcDateTime, timezone, 'YYYY-MM-DD');

/**
 * Local day a daily schedule renders on, taken from its first job. A schedule with
 * no jobs carries no instant to derive from and keeps its wire value.
 */
const dailyScheduleLocalDay = (
  schedule: { date: string; jobs?: { scheduled_start: string }[] },
  timezone: string,
): string => {
  const firstStart = schedule.jobs?.[0]?.scheduled_start;
  return firstStart ? localDay(firstStart, timezone) : schedule.date;
};

export const normalizeWindow = (rawWindow: RawWindow, timezone: string): AvailabilityWindow => {
  const [date, startTime] = fmtInZone(rawWindow.start, timezone, 'YYYY-MM-DD,HH:mm').split(',');
  const endTime = fmtInZone(rawWindow.end, timezone, 'HH:mm');
  return {
    date,
    startTime,
    endTime,
  };
};

const normalizeLunchWindow = (lunch: RawLunchTime, timezone: string): LunchWindow => {
  const [date, startTime] = fmtInZone(lunch.start, timezone, 'YYYY-MM-DD,HH:mm').split(',');
  const endTime = fmtInZone(lunch.end, timezone, 'HH:mm');
  return {
    date,
    startTime,
    endTime,
    lunchBreakDurationMinutes: lunch.lunch_duration_minutes,
  };
};

function normalizeWorkOrder(
  workOrder: RawWorkOrder,
  opts: {
    status: WorkOrderStatus;
    technicianId?: string;
    timezone: string;
    scheduledDate?: string;
    startTime?: string;
    endTime?: string;
    startDateTimeUTC?: string;
    endDateTimeUTC?: string;
    travelTimeMinutes?: number | null;
    locked?: boolean;
  },
): SchedulingWorkOrder {
  return {
    id: workOrder.work_order_uuid,
    workOrderNumber: workOrder.work_order_number,
    streetAddress: workOrder.location.street_address?.split(',')[0] ?? '',
    locationId: `wo_${workOrder.work_order_uuid}_location`,
    priority: workOrder.priority as WorkOrderPriority,
    jobType: workOrder.job_type,
    jobClassification: workOrder.job_classification,
    duration: workOrder.estimated_duration_minutes,
    locked: opts.locked ?? workOrder.locked,
    // Read from the work-order entry, never from `opts` (whose `locked` comes from the
    // job entry / the optimizer partition) — that decoupling is deliberate. Passed
    // through UNGUARDED by design: the single isSuggested/isLocked render guard lives in
    // ScheduledWOBlock. Do not add a second guard here or the two will drift.
    lockType: workOrder.lock_type ?? null,
    residentAvailabilityWindows: workOrder.resident_availability_windows.map((w) =>
      normalizeWindow(w, opts.timezone),
    ),
    createdAt: workOrder.created_at,
    status: opts.status,
    technicianId: opts.technicianId ?? null,
    scheduledDate: opts.scheduledDate ?? null,
    startTime: opts.startTime ?? null,
    endTime: opts.endTime ?? null,
    startDateTimeUTC: opts.startDateTimeUTC ?? null,
    endDateTimeUTC: opts.endDateTimeUTC ?? null,
    travelTimeMinutes: opts.travelTimeMinutes ?? null,
    serviceRequestUuid: workOrder.service_request_uuid ?? undefined,
    residentAvailabilityRequestPending:
      workOrder.resident_availability_request_pending ?? undefined,
    movedByLandon: workOrder.moved_by_landon
      ? {
          fromTechnicianName:
            workOrder.moved_by_landon.from_technician.name ?? 'another technician',
          toTechnicianName: workOrder.moved_by_landon.to_technician.name ?? 'another technician',
          fromStartDateTimeUTC: workOrder.moved_by_landon.from_start_time,
          fromEndDateTimeUTC: workOrder.moved_by_landon.from_end_time,
          residentWindowChanged: workOrder.moved_by_landon.resident_window_changed ?? false,
        }
      : null,
  };
}

// ===============================
// Location helpers
// ===============================

const addLocation = (
  locationsById: Record<string, SchedulingLocation>,
  location: SchedulingLocation,
) => {
  locationsById[location.id] ??= location;
};

// ===============================
// Main normalizer
// ===============================

export function normalizeSchedulingResponse(
  response: RawSchedulingResponse,
  timezone: string,
): SchedulingState {
  // Pre-index daily schedules for O(1) technician lookups
  const schedulesByTechId: Record<string, RawDailySchedule[]> = {};
  for (const schedule of response.existing_daily_schedules) {
    schedulesByTechId[schedule.technician_uuid] ??= [];
    schedulesByTechId[schedule.technician_uuid].push(schedule);
  }

  // Job-level lookup: work_order_uuid → scheduled time info + technician
  const jobByWorkOrderId: Record<string, { date: string; technician_uuid: string } & RawJob> = {};
  for (const schedule of response.existing_daily_schedules) {
    for (const job of schedule.jobs) {
      jobByWorkOrderId[job.work_order_uuid] = {
        ...job,
        date: localDay(job.scheduled_start, timezone),
        technician_uuid: schedule.technician_uuid,
      };
    }
  }

  // ---- Locations ----
  const locationsById: Record<string, SchedulingLocation> = {};

  // ---- Work Orders ----
  const workOrdersById: Record<string, SchedulingWorkOrder> = {};

  for (const workOrder of response.unscheduled_work_orders) {
    addLocation(locationsById, {
      id: `wo_${workOrder.work_order_uuid}_location`,
      lat: workOrder.location.latitude,
      lng: workOrder.location.longitude,
    });
    const normalized = normalizeWorkOrder(workOrder, { status: 'unassigned', timezone });
    workOrdersById[normalized.id] = normalized;
  }

  for (const workOrder of response.scheduled_work_orders) {
    addLocation(locationsById, {
      id: `wo_${workOrder.work_order_uuid}_location`,
      lat: workOrder.location.latitude,
      lng: workOrder.location.longitude,
    });
    const scheduledJob = jobByWorkOrderId[workOrder.work_order_uuid];
    const normalized = normalizeWorkOrder(workOrder, {
      status: 'assigned',
      timezone,
      technicianId: scheduledJob?.technician_uuid,
      scheduledDate: scheduledJob?.date,
      startTime: scheduledJob
        ? fmtInZone(scheduledJob.scheduled_start, timezone, 'HH:mm')
        : undefined,
      endTime: scheduledJob ? fmtInZone(scheduledJob.scheduled_end, timezone, 'HH:mm') : undefined,
      startDateTimeUTC: scheduledJob?.scheduled_start,
      endDateTimeUTC: scheduledJob?.scheduled_end,
      travelTimeMinutes: scheduledJob?.travel_time_minutes,
      locked: scheduledJob?.locked,
    });
    workOrdersById[normalized.id] = normalized;
  }

  for (const workOrder of response.awaiting_availability_work_orders) {
    addLocation(locationsById, {
      id: `wo_${workOrder.work_order_uuid}_location`,
      lat: workOrder.location.latitude,
      lng: workOrder.location.longitude,
    });
    const normalized = normalizeWorkOrder(workOrder, { status: 'awaiting', timezone });
    workOrdersById[normalized.id] = normalized;
  }

  // ---- Technicians + Schedule Blocks ----
  const techniciansById: Record<string, SchedulingTechnician> = {};
  const scheduleBlocksById: Record<string, ScheduleBlock> = {};
  const blockoutBlocksById: Record<string, BlockoutBlock> = {};

  for (const technician of response.technicians) {
    const techId = technician?.technician_uuid;
    addLocation(locationsById, {
      id: `tech_${techId}_home`,
      lat: technician?.home_location?.latitude,
      lng: technician?.home_location?.longitude,
      type: 'home',
    });
    if (technician?.hq_location?.latitude !== null && technician?.hq_location?.longitude !== null) {
      addLocation(locationsById, {
        id: `tech_${techId}_hq`,
        lat: technician?.hq_location?.latitude,
        lng: technician?.hq_location?.longitude,
        type: 'hq',
      });
    }

    const lunchBlocks: ScheduleBlock[] = technician?.lunch_time?.map((rawLunch) => {
      const [date, windowStartTime] = fmtInZone(rawLunch.start, timezone, 'YYYY-MM-DD,HH:mm').split(
        ',',
      );
      const windowEndTime = fmtInZone(rawLunch.end, timezone, 'HH:mm');
      const lunchDurationMinutes =
        rawLunch.lunch_duration_minutes ?? technician.lunch_duration_minutes;

      const techDaySchedule = (schedulesByTechId[techId] ?? []).find(
        (schedule) => dailyScheduleLocalDay(schedule, timezone) === date,
      );
      const jobsOnDate = (techDaySchedule?.jobs ?? []).map((rawJob) => ({
        startMinutes: parseTimeToMinutes(fmtInZone(rawJob.scheduled_start, timezone, 'HH:mm')),
        endMinutes: parseTimeToMinutes(fmtInZone(rawJob.scheduled_end, timezone, 'HH:mm')),
      }));

      const recommendedLunch =
        techDaySchedule?.recommended_lunch_start_time && techDaySchedule?.recommended_lunch_end_time
          ? {
              startMinutes: parseTimeToMinutes(
                fmtInZone(techDaySchedule.recommended_lunch_start_time, timezone, 'HH:mm'),
              ),
              endMinutes: parseTimeToMinutes(
                fmtInZone(techDaySchedule.recommended_lunch_end_time, timezone, 'HH:mm'),
              ),
            }
          : undefined;

      const { startMinutes, endMinutes } = computeLunchPlacement({
        windowStartMinutes: parseTimeToMinutes(windowStartTime),
        windowEndMinutes: parseTimeToMinutes(windowEndTime),
        durationMinutes: lunchDurationMinutes,
        jobs: jobsOnDate,
        recommended: recommendedLunch,
      });

      return {
        id: `lunch_${techId}_${date}`,
        technicianId: techId,
        type: 'lunch' as const,
        date,
        startTime: minutesToTime(startMinutes),
        endTime: minutesToTime(endMinutes),
      };
    });

    const blockOutBlocks: BlockoutBlock[] = (technician.block_out_windows ?? []).map((block, i) => {
      const [date, startTime] = fmtInZone(block.start, timezone, 'YYYY-MM-DD,HH:mm').split(',');
      const endTime = fmtInZone(block.end, timezone, 'HH:mm');
      const id = block.technician_appointment_id ?? `blocked_${techId}_${date}_${i}`;
      return {
        id,
        technician_appointment_id: block.technician_appointment_id,
        technicianId: techId,
        date,
        startTime,
        endTime,
        title: block.title,
        description: block.description ?? null,
        isAllDay: block.is_block_out_all_day,
        groupUuid: block.block_out_group_uuid ?? null,
        groupStartDate: block.group_start_date ?? null,
        groupEndDate: block.group_end_date ?? null,
        workOrder: block.work_order
          ? {
              workOrderId: block.work_order.work_order_id,
              workOrderNumber: block.work_order.work_order_number,
            }
          : null,
      };
    });

    for (const block of lunchBlocks) scheduleBlocksById[block.id] = block;
    for (const block of blockOutBlocks) blockoutBlocksById[block.id] = block;

    const techSchedules = schedulesByTechId[techId] ?? [];
    const assignedWorkOrderIds: string[] = [];
    const dailyStats: Record<string, TechnicianDailyStats> = {};

    for (const schedule of techSchedules) {
      assignedWorkOrderIds.push(...schedule.jobs.map((job) => job.work_order_uuid));
      const totalScheduledMinutes = schedule.jobs.reduce(
        (sum, job) => sum + job.estimated_duration_minutes,
        0,
      );
      const scheduleDate = dailyScheduleLocalDay(schedule, timezone);
      const totalPTOMinutes = blockMinutesForDate(blockOutBlocks, scheduleDate);
      dailyStats[scheduleDate] = {
        totalWOs: schedule?.jobs?.length ?? 0,
        totalWorkHours: schedule.total_work_hours,
        totalTravelHours: schedule.total_travel_hours,
        travelTimeToBaseMinutes: schedule.travel_time_to_base_minutes,
        totalScheduledMinutes,
        totalPTOMinutes,
        utilizationPercentage: calcDayUtilization(
          totalScheduledMinutes,
          totalPTOMinutes,
          technician.max_work_hours_per_day,
        ),
      };
    }

    const statsValues = Object.values(dailyStats);
    techniciansById[techId] = {
      id: techId,
      name: technician.name,
      homeLocationId: `tech_${techId}_home`,
      hqLocationId:
        technician.hq_location.latitude !== null && technician.hq_location.longitude !== null
          ? `tech_${techId}_hq`
          : undefined,
      availabilityWindows: technician.availability_windows.map((w) => normalizeWindow(w, timezone)),
      lunchWindows: technician.lunch_time.map((l) => normalizeLunchWindow(l, timezone)),
      lunchDurationMinutes: technician.lunch_duration_minutes,
      maxWorkHoursPerDay: technician.max_work_hours_per_day,
      status: technician.status,
      displayColor: technician.display_color ?? null,
      userUuid: technician.user_uuid ?? null,
      workOrderIds: assignedWorkOrderIds,
      totalUtilization:
        statsValues.length > 0
          ? Math.round(
              statsValues.reduce((sum, stat) => sum + stat.utilizationPercentage, 0) /
                statsValues.length,
            )
          : 0,
      scheduleBlockIds: lunchBlocks.map((block) => block.id),
      suggestedWOIds: [],
      dailyStats,
    };
  }

  const allTechnicianIds = toIds(techniciansById);

  // If start_date falls on a weekend with no technician activity, advance to
  // the first weekday so the toolbar doesn't open on a non-navigable date.
  const activeWeekendDates = new Set<string>(
    Object.values(techniciansById).flatMap((tech) =>
      Object.keys(tech.dailyStats).filter(isWeekend),
    ),
  );
  let firstNavigableDate = response.horizon.start_date;
  while (isWeekend(firstNavigableDate) && !activeWeekendDates.has(firstNavigableDate)) {
    firstNavigableDate = addDays(firstNavigableDate, 1);
  }

  return {
    ...schedulingInitialState,
    // Explicit, not inherited from the spread: `schedulingInitialState.timezone` is the
    // bootstrap seed, so without this key the return value would report the seed zone
    // rather than the one every field above was formatted in. The reducer used to paper
    // over that with a trailing `timezone` key in its `Object.assign`, which made the
    // correctness of the whole board depend on the key order of an object literal.
    timezone,
    horizon: {
      startDate: response.horizon.start_date,
      days: response.horizon.days,
    },
    technicians: { byId: techniciansById, allIds: allTechnicianIds },
    workOrders: { byId: workOrdersById, allIds: toIds(workOrdersById) },
    locations: { byId: locationsById, allIds: toIds(locationsById) },
    scheduleBlocks: {
      byId: scheduleBlocksById,
      allIds: toIds(scheduleBlocksById),
    },
    blockoutBlocks: {
      byId: blockoutBlocksById,
      allIds: toIds(blockoutBlocksById),
    },
    selectedTechnicianIds: allTechnicianIds,
    selectedDate: firstNavigableDate,
    pendingAvailabilityRequestUuids:
      response.availability_request_pending_service_request_uuids ?? [],
  };
}

export function setOptimizerSuggestions(
  state: SchedulingState,
  daily_schedules: DaySchedule[],
  assignments: RawAssignment[],
  unassigned: { work_order_uuid: string; reason_detail?: string }[],
  timezone: string,
) {
  for (const techId of state.technicians.allIds) {
    const tech = state.technicians.byId[techId];
    tech.suggestedWOIds = [];
    if (tech.originalDailyStats) {
      // Optimizer is re-running: restore dailyStats to pre-optimizer baseline before applying new results
      tech.dailyStats = { ...tech.originalDailyStats };
    } else {
      // First optimizer run: snapshot current dailyStats so reset can restore them
      tech.originalDailyStats = { ...tech.dailyStats };
    }
  }

  // Classify assignments: emergency WOs and their displaced (is_reschedule) WOs
  // are held in proposals and excluded from the timeline until the user approves.
  const emergencyAssignments = assignments.filter(
    (assignment) => state.workOrders.byId[assignment.work_order_uuid]?.priority === 'emergency',
  );
  // Displaced WOs are WOs bumped specifically by an emergency insertion — identified by
  // is_reschedule=true AND their previous technician matching an emergency assignment's technician.
  const emergencyTechIds = new Set(emergencyAssignments.map((a) => a.technician_uuid));
  const displacedAssignments = assignments.filter((assignment) => {
    if (!assignment.is_reschedule) return false;
    // Prefer current_appointment if provided; fall back to the WO's current technicianId in state
    const previousTechId =
      assignment.current_appointment?.technician_uuid ??
      state.workOrders.byId[assignment.work_order_uuid]?.technicianId;
    return previousTechId != null && emergencyTechIds.has(previousTechId);
  });
  const emergencyWOIds = new Set(
    emergencyAssignments.map((assignment) => assignment.work_order_uuid),
  );
  const displacedWOIds = new Set(
    displacedAssignments.map((assignment) => assignment.work_order_uuid),
  );
  const proposalWOIds = new Set([...emergencyWOIds, ...displacedWOIds]);

  // Build one proposal per emergency WO (all displaced WOs are listed as affected)
  state.emergencyRescheduleProposals = [];
  for (const emergencyAssignment of emergencyAssignments) {
    const emergencyWO = state.workOrders.byId[emergencyAssignment.work_order_uuid];
    if (!emergencyWO) continue;
    const newTech = state.technicians.byId[emergencyAssignment.technician_uuid];

    const affectedWorkOrders: EmergencyRescheduleAffectedWO[] = displacedAssignments.map(
      (displacedAssignment) => {
        const displacedWO = state.workOrders.byId[displacedAssignment.work_order_uuid];
        const oldTech = displacedWO?.technicianId
          ? state.technicians.byId[displacedWO.technicianId]
          : null;
        const assignedTech = state.technicians.byId[displacedAssignment.technician_uuid];
        return {
          id: displacedAssignment.work_order_uuid,
          address: displacedWO?.streetAddress ?? '',
          workOrderNumber: displacedWO?.workOrderNumber ?? '',
          newTechnicianId: displacedAssignment.technician_uuid,
          newTechnicianName: assignedTech?.name ?? '',
          suggestedDate: localDay(displacedAssignment.scheduled_start_utc, timezone),
          suggestedStartTime: fmtInZone(displacedAssignment.scheduled_start_utc, timezone, 'HH:mm'),
          suggestedEndTime: fmtInZone(displacedAssignment.scheduled_end_utc, timezone, 'HH:mm'),
          suggestedStartUTC: displacedAssignment.scheduled_start_utc,
          suggestedEndUTC: displacedAssignment.scheduled_end_utc,
          travelTimeMinutes: displacedAssignment.travel_time_minutes,
          oldTechnicianId: displacedWO?.technicianId ?? null,
          oldTechnicianName: oldTech?.name ?? null,
          oldScheduledDate: displacedWO?.scheduledDate ?? null,
          oldStartTime: displacedWO?.startTime ?? null,
          oldEndTime: displacedWO?.endTime ?? null,
          oldStartUTC: displacedWO?.startDateTimeUTC ?? null,
          oldEndUTC: displacedWO?.endDateTimeUTC ?? null,
        };
      },
    );

    const proposal: EmergencyRescheduleProposal = {
      id: emergencyAssignment.work_order_uuid,
      address: emergencyWO.streetAddress,
      workOrderNumber: emergencyWO.workOrderNumber,
      technicianId: emergencyAssignment.technician_uuid,
      technicianName: newTech?.name ?? '',
      scheduledDate: localDay(emergencyAssignment.scheduled_start_utc, timezone),
      startTime: fmtInZone(emergencyAssignment.scheduled_start_utc, timezone, 'HH:mm'),
      endTime: fmtInZone(emergencyAssignment.scheduled_end_utc, timezone, 'HH:mm'),
      startDateTimeUTC: emergencyAssignment.scheduled_start_utc,
      endDateTimeUTC: emergencyAssignment.scheduled_end_utc,
      travelTimeMinutes: emergencyAssignment.travel_time_minutes,
      affectedWorkOrders,
    };
    state.emergencyRescheduleProposals.push(proposal);
  }

  const durationByWoId: Record<string, number> = {};
  const localDayByWoId: Record<string, string> = {};
  for (const assignment of assignments) {
    // Build duration lookup from assignments so we can compute scheduled minutes without API utilization.
    durationByWoId[assignment.work_order_uuid] = assignment.estimated_duration_minutes;
    localDayByWoId[assignment.work_order_uuid] = localDay(assignment.scheduled_start_utc, timezone);
    // Skip WOs that are part of an emergency proposal — their state is applied on approval
    if (proposalWOIds.has(assignment.work_order_uuid)) continue;

    const wo = state.workOrders.byId[assignment.work_order_uuid];
    if (wo) {
      if (assignment.is_reschedule && !wo.preOptimizerSchedule) {
        const oldTech = wo.technicianId ? state.technicians.byId[wo.technicianId] : null;
        wo.preOptimizerSchedule = {
          technicianId: wo.technicianId,
          technicianName: oldTech?.name ?? null,
          scheduledDate: wo.scheduledDate,
          startTime: wo.startTime,
          endTime: wo.endTime,
          startDateTimeUTC: wo.startDateTimeUTC,
          endDateTimeUTC: wo.endDateTimeUTC,
        };
      }
      wo.technicianId = assignment.technician_uuid;
      wo.scheduledDate = localDayByWoId[assignment.work_order_uuid];
      wo.startTime = fmtInZone(assignment.scheduled_start_utc, timezone, 'HH:mm');
      wo.endTime = fmtInZone(assignment.scheduled_end_utc, timezone, 'HH:mm');
      wo.startDateTimeUTC = assignment.scheduled_start_utc;
      wo.endDateTimeUTC = assignment.scheduled_end_utc;
      wo.travelTimeMinutes = assignment.travel_time_minutes;
      wo.duration = assignment.estimated_duration_minutes;
      wo.isReschedule = assignment.is_reschedule ?? false;
      wo.locked = state.lockedSuggestions.some((lock) => lock.work_order_uuid === wo.id);
      // A suggestion pin is client-only and carries no WorkOrderLock row, so a work
      // order promoted to a suggestion must not keep the server lock origin it had
      // before the run. Keeps the store's own lockType-iff-locked invariant true.
      wo.lockType = null;
      wo.applyStatus = undefined;

      const locationId = `wo_${assignment.work_order_uuid}_location`;
      const location: SchedulingLocation = {
        id: locationId,
        lat: assignment.location.latitude,
        lng: assignment.location.longitude,
      };
      if (locationId in state.locations.byId) {
        state.locations.byId[locationId] = location;
      } else {
        addLocation(state.locations.byId, location);
        state.locations.allIds.push(locationId);
      }
    }
  }

  for (const daySchedule of daily_schedules) {
    const tech = state.technicians.byId[daySchedule.technician_uuid];
    if (tech) {
      const woIdSet = new Set(tech.suggestedWOIds);
      for (const job of daySchedule.jobs) {
        // Skip WOs that are part of an emergency proposal — they go to the timeline only on approval
        if (proposalWOIds.has(job.work_order_uuid)) continue;
        if (!woIdSet.has(job.work_order_uuid)) {
          woIdSet.add(job.work_order_uuid);
          tech.suggestedWOIds.push(job.work_order_uuid);
        }
      }

      // Optimizer day schedules carry only WO ids, so the local day comes from the
      // assignments they reference.
      const date =
        daySchedule.jobs.map((job) => localDayByWoId[job.work_order_uuid]).find(Boolean) ??
        daySchedule.date;
      const scheduledJobs = daySchedule.jobs.filter(
        (job) => !proposalWOIds.has(job.work_order_uuid),
      );
      const addedMinutes = scheduledJobs.reduce(
        (sum, job) =>
          sum +
          (durationByWoId[job.work_order_uuid] ??
            state.workOrders.byId[job.work_order_uuid]?.duration ??
            0),
        0,
      );

      const existing = tech.dailyStats[date];
      if (existing) {
        const newScheduledMinutes = existing.totalScheduledMinutes + addedMinutes;
        tech.dailyStats[date] = {
          ...existing,
          totalWOs: existing.totalWOs + scheduledJobs.length,
          totalScheduledMinutes: newScheduledMinutes,
          utilizationPercentage: calcDayUtilization(
            newScheduledMinutes,
            existing.totalPTOMinutes,
            tech.maxWorkHoursPerDay,
          ),
        };
      } else {
        const totalPTOMinutes = ptoMinutesFromState(state.blockoutBlocks.byId, tech.id, date);
        tech.dailyStats[date] = {
          totalWOs: scheduledJobs.length,
          totalWorkHours: daySchedule.total_work_hours,
          totalTravelHours: daySchedule.total_travel_hours,
          travelTimeToBaseMinutes: daySchedule.travel_time_to_base_minutes,
          totalScheduledMinutes: addedMinutes,
          totalPTOMinutes,
          utilizationPercentage: calcDayUtilization(
            addedMinutes,
            totalPTOMinutes,
            tech.maxWorkHoursPerDay,
          ),
        };
      }
    }
  }

  // Clear previous reason details before applying new results
  for (const id of state.workOrders.unscheduledWOIds ?? []) {
    const wo = state.workOrders.byId[id];
    if (wo) wo.unscheduledReasonDetail = undefined;
  }
  state.workOrders.unscheduledWOIds = unassigned.map((item) => item.work_order_uuid);
  for (const item of unassigned) {
    if (!item.reason_detail) continue;
    const wo = state.workOrders.byId[item.work_order_uuid];
    if (wo) wo.unscheduledReasonDetail = item.reason_detail;
  }

  // Apply optimizer-recommended lunch placements. These override the frontend-computed
  // placement set during initial normalization.
  for (const daySchedule of daily_schedules) {
    const { recommended_lunch_start_time, recommended_lunch_end_time } = daySchedule;
    if (!recommended_lunch_start_time || !recommended_lunch_end_time) continue;
    // The block was keyed by the local day of the lunch window during normalization,
    // so the recommendation has to be looked up by the local day of its own instant.
    const lunchDay = localDay(recommended_lunch_start_time, timezone);
    const lunchBlockId = `lunch_${daySchedule.technician_uuid}_${lunchDay}`;
    const lunchBlock = state.scheduleBlocks.byId[lunchBlockId];
    if (lunchBlock) {
      lunchBlock.startTime = fmtInZone(recommended_lunch_start_time, timezone, 'HH:mm');
      lunchBlock.endTime = fmtInZone(recommended_lunch_end_time, timezone, 'HH:mm');
    }
  }
}
